import * as THREE from 'three/webgpu';
import type { MeasureClosestPointResult } from '../types/measure-distance-types';
import { computeDistanceBetweenPointsMillimeter } from '../math/MeasureNumericKernel';

/** 几何退化判定容差（场景世界单位，米制坐标；用于半径、径向长度等数值比较）。 */
const MEASURE_REFERENCE_GEOMETRY_EPSILON = 1e-9;

/** 判定零距离不绘制测量线的容差（毫米）。 */
const MEASURE_REFERENCE_DISTANCE_EPSILON_MM = 0.0001;

/**
 * 圆几何描述符（世界坐标）。
 */
export interface MeasureCircleDescriptor {
    /** 圆心世界坐标。 */
    center: THREE.Vector3;
    /** 半径（世界单位）。 */
    radius: number;
    /** 圆平面单位法向。 */
    normal: THREE.Vector3;
}

/**
 * 计算两圆心之间的参考点对（中心到中心）。
 *
 * @param circleA 第一个圆（对应先选实体侧 `from`）。
 * @param circleB 第二个圆（对应后选实体侧 `to`）。
 * @returns 参考点结果。
 */
export function computeCenterToCenterReferencePoint(
    circleA: MeasureCircleDescriptor,
    circleB: MeasureCircleDescriptor,
): MeasureClosestPointResult {
    return buildMeasureReferencePointResult(circleA.center, circleB.center);
}

/**
 * 计算圆心到目标世界点的参考点对。
 *
 * @param circleSideIsFirst 圆几何体是否为先选实体。
 * @param circleCenter 圆心世界坐标。
 * @param targetPoint 目标几何体上的参考点。
 * @returns 参考点结果。
 */
export function computeCenterToTargetReferencePoint(
    circleSideIsFirst: boolean,
    circleCenter: THREE.Vector3,
    targetPoint: THREE.Vector3,
): MeasureClosestPointResult {
    if (circleSideIsFirst) {
        return buildMeasureReferencePointResult(circleCenter, targetPoint);
    }
    return buildMeasureReferencePointResult(targetPoint, circleCenter);
}

/**
 * 构建双选参考点求解结果。
 *
 * @param from 先选实体侧参考点。
 * @param to 后选实体侧参考点。
 * @returns 结果对象。
 */
export function buildMeasureReferencePointResult(
    from: THREE.Vector3,
    to: THREE.Vector3,
): MeasureClosestPointResult {
    const distance = computeDistanceBetweenPointsMillimeter(from, to);
    return {
        from: from.clone(),
        to: to.clone(),
        distance,
        shouldRenderMeasureLines: distance > MEASURE_REFERENCE_DISTANCE_EPSILON_MM,
    };
}

/**
 * 计算圆上参考点 A 的对径点（过拟合圆心，与对象存储 center 无关）。
 *
 * @param circleDescriptor 参考点 A 所在圆弧/圆的拟合描述符。
 * @param pointOnCircleSide 最小距离在圆侧的参考点 A。
 * @returns 对径点；退化时返回 null。
 */
export function computeAntipodalPointOnCircleDescriptor(
    circleDescriptor: MeasureCircleDescriptor,
    pointOnCircleSide: THREE.Vector3,
): THREE.Vector3 | null {
    const { center, radius, normal } = circleDescriptor;
    if (radius <= MEASURE_REFERENCE_GEOMETRY_EPSILON) {
        return null;
    }

    // 将 A 投影到圆平面，再沿径向取对径位置
    const offsetFromCenter = pointOnCircleSide.clone().sub(center);
    const normalOffset = normal.dot(offsetFromCenter);
    const inPlanePoint = pointOnCircleSide.clone().sub(
        normal.clone().multiplyScalar(normalOffset),
    );

    const radial = inPlanePoint.clone().sub(center);
    const radialLength = radial.length();
    if (radialLength <= MEASURE_REFERENCE_GEOMETRY_EPSILON) {
        return null;
    }

    radial.normalize();
    return center.clone().sub(radial.multiplyScalar(radius));
}
